import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import "../../styles/FeaturedModule.css";

// Highlight card component
const HighlightCard = ({ post }) => {
  const img = post?.featuredImage?.url || post?.videoThumbnail?.url || "";
  const category = post?.category?.name || post?.category || "";

  return (
    <article
      className="hm-card"
      aria-labelledby={`hm-card-title-${post?.slug || post?._id}`}
    >
      <div
        className="hm-card-thumb"
        style={{ backgroundImage: `url(${img})` }}
        role="img"
        aria-label={post?.title || "post image"}
      />
      <div className="hm-card-body">
        <h4
          id={`hm-card-title-${post?.slug || post?._id}`}
          className="hm-card-title"
        >
          {post?.title}
        </h4>
        <div className="hm-card-category text-secondary">{category}</div>

        <div className="hm-card-bottom">
          <Link
            to={`/blogs/${post?.slug}`}
            className="hm-readmore btn-secondary"
            aria-label={`Read more about ${post?.title}`}
          >
            Read More ↗
          </Link>
        </div>
      </div>
    </article>
  );
};

// FeaturedModule component
const FeaturedModule = ({ posts = [], limit = 4 }) => {
  if (!posts || posts.length === 0) return null;

  const featured = posts[0];
  const highlights = posts.slice(1, limit);

  const authorName =
    typeof featured?.author === "string"
      ? featured.author
      : `${featured?.author?.firstName || ""} ${featured?.author?.lastName || ""}`.trim();

  const category = featured?.category?.name || featured?.category || "General";

  const date = featured?.publishedDate
    ? new Date(featured.publishedDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <section className="hm-section" aria-labelledby="featured-module-heading">
      <Container fluid="lg">
        {/* Header */}
        <div className="hm-header">
          <h2 id="featured-module-heading" className="hm-header-title">
             Inside Software <span className="hm-highlight text-accent">Building </span>
          </h2>
          <p className="hm-header-subtitle">
            How software systems are designed, where they fail, and how to build them better. Focus on fundamentals and trends. Clear thinking on software engineering, product building, and technology that actually ships.
          </p>
        </div>

        {/* Featured Article */}
        <div className="hm-featured" role="region" aria-label="Featured article">
          <Row className="align-items-center">
            <Col md={5}>
              {featured?.featuredImage?.url && (
                <div
                  className="hm-featured-image"
                  style={{
                    backgroundImage: `url(${featured.featuredImage.url})`,
                    height: "300px",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: "8px",
                  }}
                  role="img"
                  aria-label={featured?.title}
                />
              )}
            </Col>

            <Col md={7}>
              <div className="hm-featured-content">
                <h3 className="hm-featured-title">{featured?.title}</h3>
                {featured?.description && (
                  <p className="hm-featured-excerpt">{featured.description}</p>
                )}

                <div className="hm-meta-row">
                  <div className="meta-col">
                    <div className="text-secondary">Category</div>
                    <div className="text-secondary">{category}</div>
                  </div>

                  <div className="meta-col">
                    <div className="text-secondary">Publication Date</div>
                    <div className="text-secondary">{date}</div>
                  </div>

                  <div className="meta-col">
                    <div className="text-secondary">Author</div>
                    <div className="text-secondary">{authorName || "Unknown"}</div>
                  </div>
                </div>

                <div className="hm-featured-cta">
                  <Link
                    to={`/blogs/${featured?.slug}`}
                    className="hm-cta-btn btn-secondary"
                    aria-label={`Read more about ${featured?.title}`}
                  >
                    Read More ↗
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Highlighted Articles */}
        <div className="hm-highlights mt-5" role="list" aria-label="Highlighted articles">
          <Row>
            {highlights.map((h) => (
              <Col md={4} key={h._id || h.slug} className="mb-4" role="listitem">
                <HighlightCard post={h} />
              </Col>
            ))}
          </Row>
        </div>
      </Container>
    </section>
  );
};
export default FeaturedModule;